// Copy for /blog/the-problem-with-ecommerce (hero meta in BlogPost.tsx + body in pages/blog/TheProblemWithEcommerce.tsx).

const en = {
  meta: {
    title: 'The Problem with E-commerce',
    titleAccent: 'E-commerce',
    subtitle: 'Revenue keeps growing, the dashboards look fine, and then the financial results arrive. Why e-commerce businesses so often discover a loss too late, and how to see it while there is still time to act.',
    seoTitle: 'The Problem with E-commerce | Stratodata Blog',
    seoDescription: 'Scalability is e-commerce\'s best friend, margin its worst enemy. A step-by-step look at how revenue turns into profit, and how a daily profit view replaces quarterly surprises.',
    category: 'Retail Profitability',
    date: 'Updated October 2026',
    readTime: '5 min read',
    author: 'Stratodata Team',
    cta: 'Talk to us about your margins',
  },
  percent: (n: number) => `${n}%`,
  percentLeft: (n: number) => `${n}% left`,
  stats: [
    { value: '35%', label: 'Gross margin left after cost of goods' },
    { value: '23%', label: 'Left after marketing' },
    { value: '10%', label: 'Left to cover fixed costs' },
    { value: 'Daily', label: 'Profit view instead of quarterly surprises' },
  ],
  journey: [
    { label: 'Revenue', text: 'Every journey to profit starts with an order and the revenue it creates.' },
    { label: 'Cost of goods sold', text: 'The obvious first step. In this example you keep 35% of sales as gross margin.' },
    { label: 'Marketing', text: 'Online, you pay to be seen. Say 12% of sales goes to marketing spend.' },
    { label: 'Delivery', text: 'Even with well-managed logistics partners, delivery costs around 7% of revenue.' },
    { label: 'Payment providers', text: 'Payment gateways take their share. A modest 1%.' },
    { label: 'Other variable costs', text: 'Website optimisation tools, packaging, warehouse dispatch, handling of returns. Another 5%.' },
  ],
  steps: [
    'Identify your sales channels and the share each one has in the total business.',
    'Map the full sales process for each channel, all the way to EBIT. Take your time.',
    'Map the data sources and the people responsible for each stage of that process.',
    'Integrate the data, present it, and watch it closely.',
    'Link it to detailed business forecasts for an even sharper view ahead.',
  ],
  dream: {
    eyebrow: 'The Dream',
    title: 'A Business That Seems to Run on Its Own',
    p1: 'At first sight, e-commerce looks like the perfect business. You know what you sell, who your customer is and who handles the logistics. What can go wrong? Quite a lot.',
    p2: 'The dream is a business that runs almost by itself, with a few managers on top putting out the occasional fire. Scale is the name of the game, so once you succeed at home, why not win new markets abroad.',
    p3: 'With the right investment, orders and revenue follow. Customer service keeps buyers happy, delivery partners cover every address, and the traffic curve in your web analytics keeps climbing, with revenue right behind it. Everything looks fine, until you start connecting the dots between the data silos in your company.',
  },
  reality: {
    eyebrow: 'The Reality',
    title: 'What Connected Data Shows',
    p1: 'A little data integration reveals the warning signs quickly. Marketing campaigns ran for months with little optimisation, so while revenue grew steadily, marketing costs grew right along with it.',
    p2: 'The spring sale brought in good orders, so prices stayed cut a little longer. Margin stayed low, maybe too low. Other things may have gone south as well: too many returns, no customer retention, high warehousing costs.',
    quote: 'Then the accounting period closes, the financial results arrive, and the business is not slightly in the red. It is deep in a loss.',
    friendLabel: 'E-commerce\'s best friend',
    friend: 'Scalability',
    enemyLabel: 'Its worst enemy',
    enemy: 'Margin',
    p3: 'Wanting to grow is legitimate. But when you chart that growth, always ask what it costs. Do you want to grow and break even? Grow and keep something at the end? Or accept losses for a while to grow faster? That can be a valid strategy, as long as you know how you will cover them.',
    p4Before: 'There is a way out of this trap. It rests on two cornerstones: ',
    p4Discipline: 'discipline',
    p4And: ' and ',
    p4Data: 'data',
    p4After: '.',
  },
  journeySection: {
    eyebrow: 'Journey to Profit',
    title: 'Peeling the Layers off Your Revenue',
    p1: 'Follow your revenue on its way to profit. The percentages below are illustrative, but typical. Except for the first step, these are all variable costs: they grow hand in hand with the scale of your business.',
  },
  opex: {
    title: '10% Left. Fixed Costs Still Unpaid.',
    p1: 'Operating expenses (OPEX) are still ahead: office space, team events and, above all, the salaries of people doing fixed work such as campaign execution, accounting and management.',
    p2: 'The math is simple. If OPEX is more than 10% of revenue, the business is making a loss.',
  },
  risk: {
    eyebrow: 'The Real Risk',
    title: 'Finding Out Too Late',
    p1: 'Does this look familiar? The biggest risk is not the cost structure itself. It is how late you discover it. Traditionally, this evaluation happens once a quarter or even once a year, with no way of knowing during the period whether you are on track.',
    p2: 'Today, that risk can be removed with well-built data integration and reporting. Instead of looking back once a quarter and hoping for the best, you evaluate daily and extrapolate your pace to the end of the period.',
  },
  approach: {
    eyebrow: 'How We Approach It',
    title: 'Five Steps to a Daily Profit View',
  },
  why: {
    eyebrow: 'Why It Matters',
    title: 'Growth You Can Afford',
    p1: 'Discipline and data do not slow growth down. They tell you, every day, whether the growth you are paying for is growth you can afford. So when the period closes, the results confirm what your team already knows, instead of taking everyone by surprise.',
  },
};

const sk: typeof en = {
  meta: {
    title: 'Problém e-commerce',
    titleAccent: 'e-commerce',
    subtitle: 'Tržby rastú, dashboardy vyzerajú dobre a potom prídu finančné výsledky. Prečo e-shopy tak často zistia stratu, až keď je neskoro, a ako ju odhaliť, kým sa s tým ešte dá niečo urobiť.',
    seoTitle: 'Problém e-commerce | Blog Stratodata',
    seoDescription: 'Škálovateľnosť je najlepší priateľ e-commerce, marža jeho najhorší nepriateľ. Krok za krokom ukazujeme, ako sa z tržieb stáva zisk a ako denný pohľad na zisk nahradí štvrťročné prekvapenia.',
    category: 'Ziskovosť v retaile',
    date: 'Aktualizované v októbri 2026',
    readTime: '5 min čítania',
    author: 'Tím Stratodata',
    cta: 'Porozprávajme sa o vašich maržiach',
  },
  percent: (n: number) => `${n} %`,
  percentLeft: (n: number) => `zostáva ${n} %`,
  stats: [
    { value: '35 %', label: 'Hrubá marža po odpočítaní nákladov na tovar' },
    { value: '23 %', label: 'Zostáva po marketingu' },
    { value: '10 %', label: 'Zostáva na fixné náklady' },
    { value: 'Denne', label: 'Pohľad na zisk namiesto štvrťročných prekvapení' },
  ],
  journey: [
    { label: 'Tržby', text: 'Každá cesta k zisku sa začína objednávkou a tržbou, ktorú prinesie.' },
    { label: 'Náklady na predaný tovar', text: 'Prvý, samozrejmý krok. V tomto príklade vám z tržieb zostane 35 % ako hrubá marža.' },
    { label: 'Marketing', text: 'Online platíte za to, aby vás bolo vidieť. Povedzme, že 12 % tržieb ide na marketing.' },
    { label: 'Doprava', text: 'Aj s dobre zvládnutými logistickými partnermi stojí doprava okolo 7 % tržieb.' },
    { label: 'Platobné brány', text: 'Platobné brány si berú svoj podiel. Skromné 1 %.' },
    { label: 'Ostatné variabilné náklady', text: 'Nástroje na optimalizáciu webu, balenie, expedícia zo skladu, spracovanie vratiek. Ďalších 5 %.' },
  ],
  steps: [
    'Určite si predajné kanály a podiel každého z nich na celom biznise.',
    'Zmapujte celý predajný proces pre každý kanál až po EBIT. Nikam sa neponáhľajte.',
    'Zmapujte dátové zdroje a ľudí zodpovedných za každú fázu tohto procesu.',
    'Dáta integrujte, zobrazte a pozorne sledujte.',
    'Prepojte ich s podrobnými biznis prognózami a dopredu uvidíte ešte ostrejšie.',
  ],
  dream: {
    eyebrow: 'Sen',
    title: 'Biznis, ktorý akoby bežal sám',
    p1: 'Na prvý pohľad vyzerá e-commerce ako dokonalý biznis. Viete, čo predávate, kto je váš zákazník a kto sa stará o logistiku. Čo sa môže pokaziť? Celkom dosť.',
    p2: 'Snom je biznis, ktorý beží takmer sám a pár manažérov na vrchu občas uhasí nejaký požiar. Všetko je o škálovaní, takže keď uspejete doma, prečo nezískať aj nové trhy v zahraničí.',
    p3: 'So správnou investíciou prídu objednávky aj tržby. Zákaznícky servis udržiava kupujúcich spokojných, doručovací partneri pokryjú každú adresu a krivka návštevnosti vo webovej analytike stále stúpa, tržby hneď za ňou. Všetko vyzerá v poriadku, kým nezačnete spájať body medzi dátovými silami vo vašej firme.',
  },
  reality: {
    eyebrow: 'Realita',
    title: 'Čo ukážu prepojené dáta',
    p1: 'Aj trocha dátovej integrácie rýchlo odhalí varovné signály. Marketingové kampane bežali mesiace takmer bez optimalizácie, takže kým tržby stabilne rástli, náklady na marketing rástli spolu s nimi.',
    p2: 'Jarný výpredaj priniesol dobré objednávky, a tak zľavy zostali o niečo dlhšie. Marža zostala nízka, možno až príliš. Pokaziť sa mohli aj ďalšie veci: priveľa vratiek, žiadne udržanie zákazníkov, vysoké náklady na skladovanie.',
    quote: 'Potom sa uzavrie účtovné obdobie, prídu finančné výsledky a firma nie je len mierne v mínuse. Je v hlbokej strate.',
    friendLabel: 'Najlepší priateľ e-commerce',
    friend: 'Škálovateľnosť',
    enemyLabel: 'Jeho najhorší nepriateľ',
    enemy: 'Marža',
    p3: 'Chcieť rásť je legitímne. Keď si však rast plánujete, vždy sa pýtajte, koľko stojí. Chcete rásť a byť na nule? Rásť a niečo si na konci nechať? Alebo nejaký čas akceptovať straty, aby ste rástli rýchlejšie? Aj to môže byť platná stratégia, pokiaľ viete, ako ich pokryjete.',
    p4Before: 'Z tejto pasce existuje cesta von. Stojí na dvoch pilieroch: ',
    p4Discipline: 'disciplíne',
    p4And: ' a ',
    p4Data: 'dátach',
    p4After: '.',
  },
  journeySection: {
    eyebrow: 'Cesta k zisku',
    title: 'Vrstva po vrstve z vašich tržieb',
    p1: 'Sledujte svoje tržby na ceste k zisku. Percentá nižšie sú ilustračné, ale typické. Okrem prvého kroku ide o variabilné náklady: rastú ruka v ruke s veľkosťou vášho biznisu.',
  },
  opex: {
    title: 'Zostáva 10 %. Fixné náklady ešte nie sú zaplatené.',
    p1: 'Ešte vás čakajú prevádzkové náklady (OPEX): kancelárie, tímové akcie a predovšetkým mzdy ľudí, ktorí robia fixnú prácu, napríklad správu kampaní, účtovníctvo a manažment.',
    p2: 'Matematika je jednoduchá. Ak je OPEX vyšší ako 10 % tržieb, firma je v strate.',
  },
  risk: {
    eyebrow: 'Skutočné riziko',
    title: 'Keď to zistíte neskoro',
    p1: 'Pripadá vám to známe? Najväčším rizikom nie je samotná štruktúra nákladov, ale to, ako neskoro ju odhalíte. Tradične sa toto vyhodnotenie robí raz za štvrťrok alebo dokonca raz za rok a počas obdobia nemáte ako zistiť, či ste na dobrej ceste.',
    p2: 'Dnes sa dá toto riziko odstrániť dobre postavenou dátovou integráciou a reportingom. Namiesto pohľadu späť raz za štvrťrok a nádeje, že to dopadne dobre, vyhodnocujete každý deň a svoje tempo premietate do konca obdobia.',
  },
  approach: {
    eyebrow: 'Ako na to ideme',
    title: 'Päť krokov k dennému pohľadu na zisk',
  },
  why: {
    eyebrow: 'Prečo na tom záleží',
    title: 'Rast, ktorý si môžete dovoliť',
    p1: 'Disciplína a dáta rast nespomaľujú. Každý deň vám povedia, či rast, za ktorý platíte, je rast, ktorý si môžete dovoliť. Keď sa potom obdobie uzavrie, výsledky len potvrdia to, čo váš tím už vie, a nikoho neprekvapia.',
  },
};

export const blogProblemWithEcommerceCopy = { en, sk };
